/* Divaniti — dynamic jewellery name & finish preview.
 * Visual-only: does not edit input value, selected variant, or cart properties.
 * Uses theme native variant-picker radios / selects; scoped to personalized products.
 */
(() => {
  if (window.__dvnPersonalisationPreviewLoaded) return;
  window.__dvnPersonalisationPreviewLoaded = true;

  const METALS = {
    gold: {
      title: 'Gold',
      color: '#eed19a',
      gradient: 'linear-gradient(145deg,#f7e5a1,#b88739 59%,#f3d990)'
    },
    silver: {
      title: 'Silver',
      color: '#d9e1ea',
      gradient: 'linear-gradient(145deg,#fff,#9ca8b4 60%,#d3dae0)'
    },
    steel: {
      title: 'Steel',
      color: '#d8e0e6',
      gradient: 'linear-gradient(145deg,#f1f3f4,#87939c 60%,#b9c4cb)'
    },
    rose: {
      title: 'Rose gold',
      color: '#e7ac9e',
      gradient: 'linear-gradient(145deg,#f9d3bd,#bb7b72 60%,#e8b5a4)'
    }
  };

  function metalFromValue(value) {
    const valueText = String(value || '').trim();
    const v = valueText.toLowerCase();
    if (/\brose[\s-]*gold\b|\brose[\s-]*gold[\s-]*color\b/.test(v)) {
      return { ...METALS.rose };
    }
    if (/\bsilver[\s-]*plated\b|\bsilverplated\b/.test(v)) {
      return { ...METALS.silver, title: 'Silver plated' };
    }
    if (/\bsliver\b|\bsilver\b|\bwhite gold\b/.test(v)) {
      return { ...METALS.silver, title: /\bwhite gold\b/.test(v) ? 'White gold tone' : 'Silver' };
    }
    if (/\bsteel\b|\bsteel[\s-]*color\b/.test(v)) {
      return { ...METALS.steel };
    }
    if (/\bgold\b|\bgolden\b|\bgold[\s-]*plated\b/.test(v)) {
      return { ...METALS.gold };
    }
    return null;
  }

  function selections(picker) {
    if (!picker) return [];
    return Array.from(picker.querySelectorAll('fieldset input:checked, select')).map((el) => {
      const selected = el instanceof HTMLSelectElement ? el.options[el.selectedIndex] : el;
      return {
        name: (selected?.dataset?.optionName || el.name || '').toLowerCase(),
        value: selected?.value || ''
      };
    }).filter((el) => el.value);
  }

  function mainPicker(root) {
    const id = root.dataset.productId;
    const all = Array.from(document.querySelectorAll('variant-picker[data-product-id]'))
      .filter((el) => el.dataset.productId === id);
    const section = root.closest('.shopify-section');
    return all.find((el) => section && section.contains(el) && el.dataset.templateProductMatch === 'true') ||
      all.find((el) => section && section.contains(el)) ||
      all.find((el) => el.dataset.templateProductMatch === 'true') || all[0];
  }

  function syncLetterRules(root, choices) {
    const input = root.querySelector('input[name="properties[Personalized name]"]');
    const hint = root.querySelector('.dvn-name-personalization__hint');
    if (!input) return;
    const mode = root.dataset.dvnPersonalizationMode || 'name';

    if (mode === 'single-initial') {
      input.maxLength = 1;
      input.minLength = 1;
      input.pattern = '[A-Za-z]';
      input.title = 'Enter exactly one letter (A–Z).';
      if (hint) hint.textContent = 'Enter exactly 1 letter (A–Z), as shown on the pendant.';
    } else if (mode === 'letter-count') {
      const selectedCount = choices.map((v) => String(v.value || '').match(/^\s*([1-5])\s*letters?\s*$/i)).find(Boolean);
      const count = selectedCount ? Number(selectedCount[1]) : 1;
      input.minLength = count;
      input.maxLength = count;
      input.pattern = '[A-Za-z]{' + count + '}';
      input.title = 'Enter exactly ' + count + ' ' + (count === 1 ? 'letter' : 'letters') + ' (A–Z).';
      input.placeholder = count === 1 ? 'e.g. R' : 'e.g. ' + 'A'.repeat(count);
      if (hint) hint.textContent = 'Enter exactly ' + count + ' ' + (count === 1 ? 'letter' : 'letters') + ' for the selected number of initial pendants.';
    } else {
      input.maxLength = 7;
      input.minLength = 1;
      input.pattern = '.*\\S.*';
      input.title = 'Enter a name of up to 7 characters.';
      if (hint) hint.textContent = 'Enter up to 7 characters, including spaces.';
    }
    // If the customer switches from multiple letters to 1 letter, enforce
    // the new maximum immediately and keep the preview in sync.
    if (input.value.length > input.maxLength) {
      input.value = input.value.slice(0, input.maxLength);
    }
  }

  function update(root) {
    if (!root || !root.isConnected) return;
    const holder = root.querySelector('[data-dvn-preview]');
    const input = root.querySelector('input[name="properties[Personalized name]"]');
    if (!holder || !input) return;

    const choices = selections(mainPicker(root));
    syncLetterRules(root, choices);
    const text = input.value.trim();
    const sample = holder.querySelector('[data-dvn-preview-name]');
    const newValue = text || holder.dataset.defaultText || 'Your name';
    if (sample && sample.textContent !== newValue) sample.textContent = newValue;
    holder.classList.toggle('is-empty', !text);

    let finish = null;
    const finishOptions = choices.filter((c) => /color|colour|finish|purity|metal/.test(c.name));
    for (const c of finishOptions.concat(choices)) {
      finish = metalFromValue(c.value);
      if (finish) break;
    }

    const finishLabel = holder.querySelector('[data-dvn-preview-finish]');
    const finishWrap = holder.querySelector('[data-dvn-preview-finish-wrap]');
    if (finish) {
      holder.style.setProperty('--dvn-preview-ink', finish.color);
      holder.style.setProperty('--dvn-preview-metal', finish.gradient);
      if (finishLabel) finishLabel.textContent = 'Finish: ' + finish.title;
      if (finishWrap) finishWrap.hidden = false;
    } else {
      holder.style.setProperty('--dvn-preview-ink', '#ece0d7');
      holder.style.setProperty('--dvn-preview-metal', 'linear-gradient(145deg,#eee5dd,#c5b9b6)');
      if (finishLabel) finishLabel.textContent = 'Finish: as selected above';
      if (finishWrap) finishWrap.hidden = false;
    }
    const extra = holder.querySelector('[data-dvn-preview-extra]');
    const chosenMonth = choices.find((c) =>
      /month|birth|style/.test(c.name) &&
      /^(january|february|march|april|may|june|july|august|september|october|november|december)$/i.test(c.value)
    );
    if (extra) {
      if (chosenMonth) extra.textContent = 'Month: ' + chosenMonth.value;
      extra.hidden = !chosenMonth;
    }
  }

  function updateAll(productId) {
    for (const root of document.querySelectorAll('[data-personalized-name-entry]')) {
      if (!productId || root.querySelector('[data-dvn-preview]')?.dataset.productId === productId) {
        update(root);
      }
    }
  }
  function init() { updateAll(); }
  document.addEventListener('input', (event) => {
    const root = event.target?.closest?.('[data-personalized-name-entry]');
    if (root) update(root);
  });
  document.addEventListener('change', (event) => {
    const picker = event.target?.closest?.('variant-picker');
    if (!picker) return;
    const productId = picker.dataset.productId;
    updateAll(productId);
    requestAnimationFrame(() => updateAll(productId));
    setTimeout(() => updateAll(productId), 300);
  });
  document.addEventListener('shopify:section:load', init);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
